
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import defensiveCopy from "@/util/defensive-copy";
import { DynamicFieldOutput } from "@/types/dynamic-data";
import DynamicInput from "@/components/Reusable/CustomInputs/DynamicInput.vue";
import {
  Condition,
  ConditionItemGroup,
  ConditionOperands,
  Rule,
  VuetifyShapeForSelect
} from "@/types/conditions";
import ConditionValuesAutocomplete from "@/components/Reusable/CustomInputs/Conditions/ConditionValuesAutocomplete.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import DynamicDataDialog from "@/components/Reusable/ControlsTree/DynamicData/DynamicDataDialog.vue";
import DynamicDataFieldText from "@/components/Reusable/ControlsTree/DynamicData/DynamicDataFieldText.vue";
import {
  getSlugFromDynamicFieldOutput,
  isDynamicShortcode
} from "@/lib/dynamic-data/utils";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import { getVuetifyShapeForDropdownFromConditionItemGroup } from "@/lib/conditions/conditions";
import Icon from "@/components/UI/Icon.vue";
import TimePickerInput from "@/components/Reusable/CustomInputs/TimePickerInput.vue";
import DatePickerInput from "@/components/Reusable/CustomInputs/DatePickerInput.vue";
import { OPERANDS } from "@/lib/conditions/conditions";
import { appendProToProConditionsLabelInFreeMode } from "@/lib/subscriptions/free-mode";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import { configStore } from "@/store/store-accessor";

@Component({
  components: {
    NumberInput,
    DatePickerInput,
    TimePickerInput,
    Icon,
    CodeInput,
    DynamicDataFieldText,
    TextInput,
    DropdownInput,
    DynamicDataDialog,
    ConditionValuesAutocomplete,
    DynamicInput
  }
})
export default class TemplateRuleInput extends Vue {
  @Prop()
  value!: Rule;

  @Prop()
  availableConditions!: Condition[];

  @Prop()
  usedConditions!: Condition[];

  @Prop()
  templateTypes!: string[];

  isDynamicDataOpen = false;

  saveDynamicDataOnFieldKey: "ruleDynamic" | "value" | null = null;

  defaultCustomPHP =
    "global $post;\nif ($post && $post->post_title === 'Sample Page') {\n  return true;\n} else {\n  return false;\n}";

  controlValues = {
    ruleAttributes: {},
    valueAttributes: {}
  };

  get OPERANDS() {
    return OPERANDS;
  }

  get ruleDynamicField() {
    if (!this.value?.ruleDynamicMeta) return null;

    const slug = getSlugFromDynamicFieldOutput(this.value.ruleDynamicMeta);
    return configStore.dynamicFields.find(field => field.slug === slug);
  }

  get ruleValueField() {
    if (!this.value?.valueDynamicMeta) return null;

    const slug = getSlugFromDynamicFieldOutput(this.value.valueDynamicMeta);
    return configStore.dynamicFields.find(field => field.slug === slug);
  }

  get isDynamicValue() {
    if (Array.isArray(this.value.value)) {
      return false;
    }

    return isDynamicShortcode(this.value.value);
  }

  get availableConditionsForDropdownInVuetifyShape(): VuetifyShapeForSelect[] {
    const items: VuetifyShapeForSelect[] = [];
    const conditions = this.availableConditionsForDropdown;
    const categories = [...new Set(conditions.map(c => c.category))];

    categories.forEach(category => {
      items.push({ header: category });
      items.push(...conditions.filter(c => c.category === category));
      items.push({ divider: true });
    });
    items.pop();

    return items;
  }

  get availableConditionsForDropdown() {
    return this.availableConditions.map(condition => {
      // https://github.com/soflyy/breakdance/pull/6365
      // const disabled =
      //   this.usedConditions.map(c => c.slug).includes(condition.slug) &&
      //   this.value.ruleSlug !== condition.slug &&
      //   condition.slug !== "dynamic-data";

      let text = condition.label;

      // Show extra text in the dropdown for dynamic data to improve UX
      if (condition.slug === "dynamic-data" && this.ruleDynamicField) {
        const attributes = this.value?.ruleDynamicMeta?.attributes ?? {};

        const attributesString = Object.values(attributes)
          .filter(v => typeof v === "string")
          .map(v => v)
          .join(", ");

        text = `Dynamic Data: ${this.ruleDynamicField.label} ${
          attributesString ? `(${attributesString})` : ""
        }`;
      }

      return {
        text: appendProToProConditionsLabelInFreeMode(text, condition),
        value: condition.slug,
        category: condition.category,
        // disabled: disabled
        // https://github.com/soflyy/breakdance/pull/6365
        disabled: false
      };
    });
  }

  get availableOperandsForDropdown() {
    if (!this.value.ruleSlug) return [];

    return getOperandsForCondition(
      this.value.ruleSlug,
      this.availableConditions
    ).map(operand => {
      return {
        text: operand,
        value: operand
      };
    });
  }

  get valueInputType() {
    return this.availableConditions.find(
      condition => condition.slug === this.value.ruleSlug
    )?.valueInputType;
  }

  get availableValuesForDropdown() {
    if (!this.value.ruleSlug) return false;

    const values = getValuesForCondition(
      this.value.ruleSlug,
      this.availableConditions
    );

    if (!values || values.length === 0) return false;

    return {
      valueType: this.valueInputType,
      values
    };
  }

  get isDynamicDataRule() {
    return (
      this.value.ruleSlug === "dynamic-data" &&
      isDynamicShortcode(this.value.ruleDynamic)
    );
  }

  setCondition(ruleSlug: string | undefined) {
    const newRule = defensiveCopy(this.value);
    newRule.ruleSlug = ruleSlug;

    /* clear the rule when it changes or is cleared */
    if (this.value.ruleSlug !== newRule.ruleSlug) {
      newRule.operand = undefined;
      newRule.value = undefined;
      delete newRule.ruleDynamic;
      delete newRule.ruleDynamicMeta;
      delete newRule.valueDynamicMeta;
    }

    /* set the operand to the first available operand if the operand is blank */
    if (newRule.ruleSlug && !newRule.operand) {
      const firstOperand = defensiveCopy(
        getOperandsForCondition(newRule.ruleSlug, this.availableConditions)
      ).shift();

      newRule.operand = firstOperand;
    }

    if (newRule.ruleSlug === "custom-php") {
      newRule.value = this.defaultCustomPHP;
    } else if (newRule.ruleSlug === "dynamic-data" && !newRule.ruleDynamic) {
      this.isDynamicDataOpen = true;
      this.saveDynamicDataOnFieldKey = "ruleDynamic";
    }

    this.update(newRule);
  }

  setOperand(operand: ConditionOperands | undefined) {
    const newRule = defensiveCopy(this.value);
    newRule.operand = operand;
    this.update(newRule);
  }

  setValue(value: string | undefined | null) {
    const newRule = defensiveCopy(this.value);
    newRule.value = value ?? undefined;
    this.update(newRule);
  }

  update(newRule: Rule) {
    this.$emit("input", newRule);
  }

  // Dynamic Data

  openDynamicData(saveDynamicDataOnFieldKey: "ruleDynamic" | "value") {
    this.saveDynamicDataOnFieldKey = saveDynamicDataOnFieldKey;
    this.isDynamicDataOpen = true;
  }

  closeDynamicData() {
    this.saveDynamicDataOnFieldKey = null;
    this.isDynamicDataOpen = false;
  }

  updateDynamicRule(output: DynamicFieldOutput) {
    const newRule = defensiveCopy(this.value);
    newRule.ruleDynamic = output.shortcode;
    newRule.ruleDynamicMeta = output;
    this.update(newRule);
  }

  updateDynamicValue(output: DynamicFieldOutput) {
    const newRule = defensiveCopy(this.value);
    newRule.value = output.shortcode;
    newRule.valueDynamicMeta = output;
    this.update(newRule);
  }

  setValueAsDynamicField(output: DynamicFieldOutput) {
    if (!this.saveDynamicDataOnFieldKey) return;

    if (this.saveDynamicDataOnFieldKey === "ruleDynamic") {
      this.updateDynamicRule(output);
    }

    if (this.saveDynamicDataOnFieldKey === "value") {
      this.updateDynamicValue(output);
    }
  }

  setDynamicRuleInValue(dynamicFieldOutput: DynamicFieldOutput) {
    const newRule = defensiveCopy(this.value);
    newRule.ruleDynamic = dynamicFieldOutput.shortcode;
    this.update(newRule);
  }

  get dynamicDataMeta() {
    if (this.saveDynamicDataOnFieldKey === "ruleDynamic") {
      return this.value.ruleDynamicMeta;
    }
    if (this.saveDynamicDataOnFieldKey === "value") {
      return this.value.valueDynamicMeta;
    }
    return "";
  }

  get dynamicDataFieldAttributes() {
    if (this.saveDynamicDataOnFieldKey === "ruleDynamic") {
      return this.value?.ruleDynamicMeta?.attributes;
    }
    if (this.saveDynamicDataOnFieldKey === "value") {
      return this.value?.valueDynamicMeta?.attributes;
    }
    return null;
  }

  getVuetifyShapeForValues(items: ConditionItemGroup[]) {
    return getVuetifyShapeForDropdownFromConditionItemGroup(items);
  }
}

/* helper functions */
function getOperandsForCondition(
  conditionSlug: string,
  conditions: Condition[]
) {
  const operands = conditions.find(
    condition => condition.slug === conditionSlug
  )?.operands;

  return operands || [];
}

function getValuesForCondition(conditionSlug: string, conditions: Condition[]) {
  const values = conditions.find(
    condition => condition.slug === conditionSlug
  )?.values;

  return values || false;
}
